import { OrbLoadingState } from '@src/components/common/OrbLoadingState';
import React from 'react';
import styled from 'styled-components';

type Props = {
  text?: string | React.ReactNode;
  onHide?: () => void;
  hideText?: string;
  /**
   * Accessible name for the indicator. Defaults to `text` when that is a
   * plain string — the orb is a canvas, so without a name it is invisible to
   * assistive tech.
   */
  ariaLabel?: string;
};

/**
 * The blocking overlay: the solving orb and its label, centred on an opaque
 * app-background field, for the moment the whole screen is genuinely unusable —
 * boot, auth handoff, or a result the entire view depends on.
 *
 * It carries the orb rather than a ring spinner. A spinner promises a moment,
 * and everything that reaches this overlay — booting, generating an article,
 * saving a batch of clips — takes considerably longer than a moment. Where a
 * real percentage exists, a determinate ProgressBar beats both.
 *
 * The orb and its label live in `OrbLoadingState`, so this is that component on
 * a full-viewport field. If the wait belongs to one region and the rest of the
 * shell is still useful, render `OrbLoadingState` there instead of this.
 */
const FullScreenLoadingOverlay = ({
  text,
  onHide,
  hideText = 'Hide',
  ariaLabel,
}: Props) => (
  <Overlay>
    <OrbLoadingState
      text={text}
      onHide={onHide}
      hideText={hideText}
      ariaLabel={ariaLabel}
    />
  </Overlay>
);

export default FullScreenLoadingOverlay;

// Opaque `--background`, not a translucent scrim. This covers the whole screen,
// so there is nothing to see through: the design system's own rule is that a
// scrim must actually obscure, and a see-through one reads as two overlapping
// screens. It was `rgba(0, 0, 0, 0.7)`, which only looked right on top of an
// already-dark app — on the boot and auth gates there is no app behind it yet,
// so 70% black composited over the white body and the screen came out grey.
const Overlay = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: var(--background);
  z-index: 1000010;
`;
